import React from 'react';
import {
    Stack,
    Typography,
    Select,
    MenuItem,
    IconButton,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ActionButton } from '../../../components/Button';

interface UserTableFilterProps {
    selectedCount: number;
    onClose: () => void;
    onApply: (action: string) => void;
    isAnimating?: boolean;
    loading?: boolean;
}

const UserTableFilter: React.FC<UserTableFilterProps> = ({
    selectedCount,
    onClose,
    onApply,
    isAnimating = false,
    loading = false,
}) => {
    const [selectedAction, setSelectedAction] = React.useState('unblock');

    const handleApply = () => {
        onApply(selectedAction);
    };

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={{ xs: 1.5, sm: 2 }}
            bgcolor="#EEF1FF"
            px={{ xs: 2, sm: 3 }}
            py={{ xs: 1.5, sm: 2 }}
            borderRadius={{ sm: "9999px", xs: "14px" }}
            mb={3}
            sx={{
                animation: isAnimating 
                    ? 'slideUp 0.3s ease-in forwards'
                    : 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                    '0%': {
                        opacity: 0,
                        transform: 'translateY(-20px)',
                        maxHeight: 0,
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                        maxHeight: '200px',
                    },
                },
                '@keyframes slideUp': {
                    '0%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                        maxHeight: '200px',
                    },
                    '100%': {
                        opacity: 0,
                        transform: 'translateY(-20px)',
                        maxHeight: 0,
                    },
                },
                overflow: 'hidden',
            }}
        >
            {/* Left: Selected Text with Close Button */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                width="100%"
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            p: '4px',
                            color: '#000',
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Typography
                        fontFamily="Poppins, sans-serif"
                        fontSize={{ xs: 12, sm: 14 }}
                        fontWeight={600}
                    >
                        {selectedCount} Selected
                    </Typography>
                </Stack>
            </Stack>

            {/* Center: Dropdown */}
            <Select
                size="small"
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                disabled={loading}
                fullWidth
                sx={{
                    height: 36,
                    borderRadius: '16px',
                    fontSize: 14,
                    fontFamily: 'Poppins, sans-serif',
                    background: '#fff',
                    px: 1,
                    border: 'none',
                    width: { xs: '100%', sm: 'auto' },
                }}
            >
                <MenuItem value="unblock">Unblock Selected Users</MenuItem>
                <MenuItem value="block">Block Selected Users</MenuItem>
            </Select>

            {/* Right: Apply Button */}
            <ActionButton
                onClick={handleApply}
                disabled={loading}
                fullWidth
                sx={{
                    backgroundColor: '#132F5F',
                    px: 3,
                    height: 38,
                    fontSize: 16,
                    fontWeight: 500,
                    fontFamily: 'Poppins, sans-serif',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                        backgroundColor: '#132F5F',
                    },
                    '&:disabled': {
                        backgroundColor: '#ccc',
                    },
                }}
            >
                {loading ? (
                    <CircularProgress size={20} color="inherit" />
                ) : (
                    'Apply'
                )}
            </ActionButton>
        </Stack>
    );
};

export default UserTableFilter; 